<template>
  <div class="na-page na-staff dash">
    <StaffHeader role="Staff" />

    <div class="st-wrap st-below">
      <!-- Heading -->
      <div class="st-head">
        <div>
          <div class="st-kicker">{{ today }}</div>
          <h1 class="st-title">{{ greeting }}, <em>{{ firstName }}.</em></h1>
          <p class="st-sub">Here's where your towns and papers stand today.</p>
        </div>
        <div class="head-actions">
          <router-link to="/staff-leads" class="st-btn st-btn-primary"><v-icon size="18">mdi-account-plus-outline</v-icon>Add a lead</router-link>
          <router-link to="/staff-onboard-customer" class="st-btn st-btn-outline"><v-icon size="18">mdi-rocket-launch-outline</v-icon>Onboard a customer</router-link>
          <router-link to="/staff/blog" class="st-btn st-btn-outline"><v-icon size="18">mdi-post-outline</v-icon>Write a blog post</router-link>
        </div>
      </div>

      <!-- KPIs -->
      <div class="kpis">
        <div v-for="k in kpis" :key="k.label" class="kpi st-card">
          <div class="kpi-top">
            <span class="kpi-label">{{ k.label }}</span>
            <span class="kpi-icon"><v-icon size="20" color="#fff">{{ k.icon }}</v-icon></span>
          </div>
          <div class="kpi-value">{{ k.value === null ? '—' : k.value.toLocaleString() }}</div>
          <div class="kpi-meta">{{ k.meta }}</div>
        </div>
      </div>

      <div class="grid-main">
        <!-- Today's focus -->
        <section class="st-card focus">
          <div class="st-card-head">
            <h2 class="st-card-title"><v-icon size="22">mdi-calendar-check-outline</v-icon>Today's focus</h2>
            <span class="st-count">{{ focus.length }}</span>
          </div>
          <div v-if="!focus.length" class="st-empty small-empty">
            <v-icon size="36">mdi-check-all</v-icon>
            <h3>You're all caught up</h3>
            <p>No follow-ups are due today and there are no new demo requests. Add a lead or set a next action to plan tomorrow.</p>
          </div>
          <ul v-else class="focus-list">
            <li v-for="f in focus" :key="f.id">
              <router-link :to="f.to" class="focus-item" :class="{ overdue: f.overdue }">
                <span class="focus-icon" :class="f.kind"><v-icon size="18" color="#fff">{{ f.icon }}</v-icon></span>
                <span class="focus-text">
                  <span class="focus-title">{{ f.title }}</span>
                  <span class="focus-sub">{{ f.sub }}</span>
                </span>
                <span class="st-badge" :class="f.overdue ? 'red' : f.kind === 'request' ? 'brass' : 'navy'">{{ f.badge }}</span>
              </router-link>
            </li>
          </ul>
          <div class="card-foot">
            <router-link to="/staff-leads" class="st-btn st-btn-ghost st-btn-sm">Open leads<v-icon size="16">mdi-arrow-right</v-icon></router-link>
          </div>
        </section>

        <!-- Pipeline by stage -->
        <section class="st-card">
          <div class="st-card-head">
            <h2 class="st-card-title"><v-icon size="22">mdi-chart-timeline-variant</v-icon>Pipeline</h2>
            <span class="st-muted">{{ openLeads.length }} open deals</span>
          </div>
          <div class="stages">
            <div v-for="s in stageCounts" :key="s.key" class="stage-row">
              <span class="stage-name">{{ s.label }}</span>
              <span class="stage-bar"><span class="stage-fill" :style="{ width: s.pct + '%' }"></span></span>
              <span class="stage-num">{{ s.count }}</span>
            </div>
          </div>

          <div class="spark-head">Last 7 days</div>
          <div class="sparks">
            <div v-for="sp in sparks" :key="sp.label" class="spark">
              <div class="spark-bars">
                <span v-for="(v, i) in sp.values" :key="i" class="spark-bar" :style="{ height: barHeight(v, sp.values) }" :title="v + ''"></span>
              </div>
              <div class="spark-label">{{ sp.label }}</div>
              <div class="spark-total">{{ sp.values.reduce((a, b) => a + b, 0) }}</div>
            </div>
          </div>
        </section>
      </div>

      <div class="grid-second">
        <!-- Leaderboard -->
        <section class="st-card">
          <div class="st-card-head">
            <h2 class="st-card-title"><v-icon size="22">mdi-trophy-outline</v-icon>Team scoreboard</h2>
            <span class="st-muted">Points from calls, demos and wins</span>
          </div>
          <div v-if="leaderboardLoading" class="st-empty"><div class="st-loader"></div></div>
          <div v-else-if="!leaderboard.length" class="st-empty small-empty">
            <v-icon size="36">mdi-podium</v-icon>
            <h3>No points yet</h3>
            <p>Points appear as the team adds leads, logs calls and books demos.</p>
          </div>
          <ol v-else class="board">
            <li v-for="(m, i) in leaderboard" :key="m.id" class="board-row">
              <span class="board-rank" :class="'r' + Math.min(i + 1, 4)">{{ i + 1 }}</span>
              <span class="board-avatar">{{ m.initials }}</span>
              <span class="board-info">
                <span class="board-name">{{ m.name }}<span v-if="m.id === uid" class="you">You</span></span>
                <span class="board-meta">{{ m.wins }} won <span aria-hidden="true">·</span> {{ m.demos }} demos</span>
              </span>
              <span class="board-points">{{ m.points.toLocaleString() }}<small>pts</small></span>
            </li>
          </ol>
        </section>

        <!-- Shortcuts -->
        <section class="st-card shortcuts">
          <div class="st-card-head">
            <h2 class="st-card-title"><v-icon size="22">mdi-lightning-bolt-outline</v-icon>Tools</h2>
          </div>
          <router-link v-for="t in tools" :key="t.to" :to="t.to" class="tool">
            <span class="tool-icon"><v-icon size="20" color="#14315E">{{ t.icon }}</v-icon></span>
            <span class="tool-text"><b>{{ t.title }}</b><span>{{ t.text }}</span></span>
            <v-icon size="18" class="tool-arrow">mdi-chevron-right</v-icon>
          </router-link>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import StaffHeader from '@/components/StaffHeader.vue'
import { auth, db } from '@/firebase'
import { collection, collectionGroup, doc, getDoc, limit, onSnapshot, orderBy, query, where } from 'firebase/firestore'
import { STAGES, OPEN_STAGES, NEXT_ACTIONS } from '@/salesConfig'
import '@/assets/na-staff.css'

const DAY = 864e5
const toMs = (t) => (t && t.seconds ? t.seconds * 1000 : t && t.toDate ? t.toDate().getTime() : t ? new Date(t).getTime() : 0)
const startOfDay = (d = new Date()) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x }
const weekStart = () => new Date(startOfDay().getTime() - 6 * DAY)
const bucket7 = () => [0, 0, 0, 0, 0, 0, 0]
const dayIndex = (ms) => { const i = Math.floor((startOfDay(new Date(ms)).getTime() - weekStart().getTime()) / DAY); return i >= 0 && i < 7 ? i : -1 }

export default {
  name: 'StaffDashboardView',
  components: { StaffHeader },
  data: () => ({
    uid: null,
    firstName: 'there',
    customers: null,
    openLeads: [],
    wonLeads: [],
    requests: [],
    sparkInbound: bucket7(),
    sparkProgress: bucket7(),
    sparkDemos: bucket7(),
    leaderboard: [],
    leaderboardLoading: true,
    unsubs: [],
    tools: [
      { to: '/staff-leads', icon: 'mdi-account-search-outline', title: 'Leads', text: 'Towns, papers and the people who decide' },
      { to: '/staff/templates', icon: 'mdi-email-edit-outline', title: 'Email templates', text: 'Outreach for mayors, managers, clerks and publishers' },
      { to: '/staff/blog', icon: 'mdi-post-outline', title: 'Blog', text: 'Publish articles to the NewsAppify blog' },
      { to: '/staff-payments', icon: 'mdi-credit-card-outline', title: 'Payments', text: 'Your payouts and statements' },
      { to: '/staff-onboard-customer', icon: 'mdi-rocket-launch-outline', title: 'Onboard a customer', text: 'Start a new town\'s setup' },
    ],
  }),
  computed: {
    today() { return new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) },
    greeting() { const h = new Date().getHours(); return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening' },
    wonThisMonth() {
      const now = new Date()
      return this.wonLeads.filter(l => { const d = new Date(toMs(l.closedAt)); return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear() }).length
    },
    kpis() {
      return [
        { label: 'Active customers', value: this.customers, meta: 'Towns and papers live on NewsAppify', icon: 'mdi-newspaper-variant-multiple-outline' },
        { label: 'Open deals', value: this.openLeads.length, meta: `${this.openLeads.filter(l => l.ownerUid === this.uid).length} are yours`, icon: 'mdi-briefcase-outline' },
        { label: 'New demo requests', value: this.requests.length, meta: 'From the website, waiting for a reply', icon: 'mdi-email-alert-outline' },
        { label: 'Won this month', value: this.wonThisMonth, meta: `${this.wonLeads.length} won all-time`, icon: 'mdi-trophy-outline' },
      ]
    },
    stageCounts() {
      const open = STAGES.filter(s => s.open && (s.key !== 'qualified' || this.openLeads.some(l => l.status === 'qualified')))
      const counts = open.map(s => ({ ...s, count: this.openLeads.filter(l => l.status === s.key).length }))
      const max = Math.max(1, ...counts.map(c => c.count))
      return counts.map(c => ({ ...c, pct: Math.round((c.count / max) * 100) }))
    },
    sparks() {
      return [
        { label: 'New leads', values: this.sparkInbound },
        { label: 'Calls, emails & meetings', values: this.sparkProgress },
        { label: 'Demos & proposals due', values: this.sparkDemos },
      ]
    },
    focus() {
      const endToday = startOfDay().getTime() + DAY
      const now = Date.now()
      const label = (k) => (NEXT_ACTIONS.find(a => a.key === k) || {}).label || k
      const actions = this.openLeads
        .filter(l => l.ownerUid === this.uid && l.nextAction && l.nextAction.dueAt && toMs(l.nextAction.dueAt) < endToday)
        .sort((a, b) => toMs(a.nextAction.dueAt) - toMs(b.nextAction.dueAt))
        .map(l => {
          const due = toMs(l.nextAction.dueAt)
          return {
            id: 'l' + l.id,
            kind: 'action',
            icon: 'mdi-phone-outline',
            title: `${label(l.nextAction.type)} — ${l.companyName || 'Lead'}`,
            sub: [l.contactName, l.contactRole].filter(Boolean).join(', ') || l.nextAction.note || 'Follow up',
            overdue: due < now,
            badge: due < now ? 'Overdue' : new Date(due).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
            to: { path: '/staff-leads', query: { lead: l.id } },
          }
        })
      const reqs = this.requests.slice(0, 5).map(r => ({
        id: 'r' + r.id,
        kind: 'request',
        icon: 'mdi-email-alert-outline',
        title: `Demo request — ${r.town || r.companyName || 'Website'}`,
        sub: [r.name, r.role].filter(Boolean).join(', ') || r.email || '',
        overdue: false,
        badge: 'Reply',
        to: { path: '/staff-leads', query: { request: r.id } },
      }))
      return [...actions, ...reqs].slice(0, 8)
    },
  },
  mounted() {
    const u = auth.currentUser
    this.uid = u ? u.uid : null
    if (u) this.loadName(u)
    const sub = (q, fn) => {
      try { this.unsubs.push(onSnapshot(q, fn, (e) => console.warn('Dashboard listener:', e.code || e.message))) } catch (e) { console.warn(e) }
    }
    sub(collection(db, 'customers'), s => { this.customers = s.size })
    sub(query(collection(db, 'leads'), where('status', 'in', OPEN_STAGES)), s => { this.openLeads = s.docs.map(d => ({ id: d.id, ...d.data() })) })
    sub(query(collection(db, 'leads'), where('status', '==', 'won')), s => { this.wonLeads = s.docs.map(d => ({ id: d.id, ...d.data() })) })
    sub(query(collection(db, 'contact_requests'), where('status', '==', 'new')), s => {
      this.requests = s.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => toMs(b.createdAt) - toMs(a.createdAt))
    })
    sub(query(collection(db, 'leads'), where('createdAt', '>=', weekStart())), s => {
      const b = bucket7(); s.forEach(d => { const i = dayIndex(toMs(d.data().createdAt)); if (i >= 0) b[i]++ }); this.sparkInbound = b
    })
    sub(query(collectionGroup(db, 'interactions'), where('at', '>=', weekStart())), s => {
      const b = bucket7(); s.forEach(d => { const i = dayIndex(toMs(d.data().at)); if (i >= 0) b[i]++ }); this.sparkProgress = b
    })
    sub(query(collection(db, 'leads'), where('nextAction.dueAt', '>=', weekStart())), s => {
      const b = bucket7()
      s.forEach(d => {
        const n = d.data().nextAction
        if (!n || !['demo', 'proposal'].includes(String(n.type || '').toLowerCase())) return
        const i = dayIndex(toMs(n.dueAt)); if (i >= 0) b[i]++
      })
      this.sparkDemos = b
    })
    sub(query(collection(db, 'user_stats'), orderBy('points', 'desc'), limit(10)), async s => {
      this.leaderboardLoading = true
      this.leaderboard = await Promise.all(s.docs.map(d => this.hydrate(d.id, d.data() || {})))
      this.leaderboardLoading = false
    })
  },
  beforeDestroy() { this.unsubs.forEach(u => u && u()) },
  methods: {
    async loadName(u) {
      this.firstName = (u.displayName || u.email || 'there').split(/[\s@]/)[0]
      try {
        const s = await getDoc(doc(db, 'users', u.uid))
        if (s.exists() && (s.data().firstName || s.data().fullName)) this.firstName = (s.data().firstName || s.data().fullName).split(' ')[0]
      } catch (e) { /* keep */ }
    },
    async hydrate(uid, stats) {
      let name = 'Team member'
      try {
        const s = await getDoc(doc(db, 'users', uid))
        if (s.exists()) {
          const d = s.data()
          name = `${d.firstName || ''} ${d.lastName || ''}`.trim() || d.fullName || d.displayName || d.email || name
        }
      } catch (e) { /* keep */ }
      return {
        id: uid,
        name,
        initials: name.split(/\s+/).map(p => p[0]).join('').slice(0, 2).toUpperCase(),
        points: Number(stats.points || 0),
        wins: Number(stats.wins || 0),
        demos: Number(stats.demos || 0),
      }
    },
    barHeight(v, values) {
      const max = Math.max(1, ...values)
      return Math.max(6, Math.round((v / max) * 100)) + '%'
    },
  },
}
</script>

<style scoped>
.head-actions { display: flex; gap: 10px; flex-wrap: wrap; }

.kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 18px; margin-bottom: 22px; }
.kpi { padding: 22px 24px; }
.kpi-top { display: flex; justify-content: space-between; align-items: center; }
.kpi-label { font-size: 0.78rem; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: var(--na-ink-2); }
.kpi-icon { width: 38px; height: 38px; border-radius: 50%; background: var(--na-navy); display: flex; align-items: center; justify-content: center; box-shadow: inset 0 0 0 2.5px var(--na-navy), inset 0 0 0 3.5px rgba(255, 255, 255, 0.5); }
.kpi-value { font-family: var(--na-serif); font-weight: 700; font-size: 2.8rem; line-height: 1; margin: 14px 0 6px; color: var(--na-navy); }
.kpi-meta { font-size: 0.88rem; color: var(--na-ink-2); }

.grid-main { display: grid; grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr); gap: 22px; margin-bottom: 22px; }
.grid-second { display: grid; grid-template-columns: minmax(0, 1.2fr) minmax(0, 0.8fr); gap: 22px; }

/* Focus */
.focus-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 8px; }
.focus-item { display: flex; align-items: center; gap: 14px; padding: 12px 14px; border: 1px solid var(--na-rule); border-radius: 9px; background: #fff; text-decoration: none; color: var(--na-ink); transition: border-color 0.15s ease; }
.focus-item:hover { border-color: var(--na-navy); }
.focus-item.overdue { border-left: 4px solid var(--na-press); }
.focus-icon { width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; background: var(--na-navy); }
.focus-icon.request { background: var(--na-brass); }
.focus-text { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.focus-title { font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.focus-sub { font-size: 0.88rem; color: var(--na-ink-2); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.card-foot { margin-top: 16px; padding-top: 12px; border-top: 1px solid var(--na-rule); }
.small-empty { padding: 28px 12px; }

/* Pipeline */
.stages { display: flex; flex-direction: column; gap: 10px; }
.stage-row { display: grid; grid-template-columns: 170px 1fr 34px; gap: 12px; align-items: center; }
.stage-name { font-size: 0.93rem; color: var(--na-ink); }
.stage-bar { height: 10px; background: #EFE8DA; border-radius: 5px; overflow: hidden; }
.stage-fill { display: block; height: 100%; background: var(--na-navy); border-radius: 5px; min-width: 2px; transition: width 0.4s ease; }
.stage-num { font-weight: 700; text-align: right; color: var(--na-navy); }
.spark-head { margin: 24px 0 10px; padding-top: 16px; border-top: 1px solid var(--na-rule); font-size: 0.75rem; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: var(--na-ink-2); }
.sparks { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
.spark-bars { display: flex; align-items: flex-end; gap: 4px; height: 64px; padding: 6px; background: #F6F0E3; border-radius: 6px; }
.spark-bar { flex: 1; background: var(--na-brass); border-radius: 3px 3px 0 0; }
.spark-label { font-size: 0.82rem; color: var(--na-ink-2); margin-top: 8px; line-height: 1.3; }
.spark-total { font-family: var(--na-serif); font-weight: 700; font-size: 1.4rem; color: var(--na-ink); }

/* Board */
.board { list-style: none; padding: 0; margin: 0; }
.board-row { display: flex; align-items: center; gap: 14px; padding: 11px 4px; border-top: 1px solid #EFE8DA; }
.board-row:first-child { border-top: 0; }
.board-rank { width: 26px; font-family: var(--na-serif); font-weight: 700; font-size: 1.2rem; text-align: center; color: var(--na-ink-2); }
.board-rank.r1 { color: var(--na-brass-text); }
.board-avatar { width: 38px; height: 38px; border-radius: 50%; background: var(--na-navy); color: #fff; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 0.82rem; flex-shrink: 0; }
.board-info { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.board-name { font-weight: 600; display: flex; align-items: center; gap: 8px; }
.you { font-size: 0.7rem; padding: 1px 7px; border-radius: 999px; background: #F2EADB; color: var(--na-brass-text); font-weight: 700; }
.board-meta { font-size: 0.85rem; color: var(--na-ink-2); }
.board-points { font-family: var(--na-serif); font-weight: 700; font-size: 1.3rem; color: var(--na-navy); }
.board-points small { font-family: var(--na-sans); font-size: 0.75rem; font-weight: 500; color: var(--na-ink-2); margin-left: 3px; }

/* Tools */
.tool { display: flex; align-items: center; gap: 14px; padding: 12px 6px; border-top: 1px solid #EFE8DA; text-decoration: none; color: var(--na-ink); }
.tool:first-of-type { border-top: 0; }
.tool:hover .tool-arrow { transform: translateX(3px); color: var(--na-navy) !important; }
.tool-icon { width: 38px; height: 38px; border-radius: 50%; background: #EEE6D6; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.tool-text { flex: 1; display: flex; flex-direction: column; }
.tool-text b { font-weight: 600; }
.tool-text span { font-size: 0.86rem; color: var(--na-ink-2); }
.tool-arrow { transition: transform 0.15s ease; }

@media (max-width: 1100px) { .kpis { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 900px) {
  .grid-main, .grid-second { grid-template-columns: 1fr; }
  .stage-row { grid-template-columns: 130px 1fr 30px; }
}
@media (max-width: 560px) {
  .kpis { grid-template-columns: 1fr; }
  .sparks { grid-template-columns: 1fr; }
  .head-actions .st-btn { width: 100%; }
}
</style>